import { AxiosResponse } from "axios";
import { apiErrorWithMessages } from "../lib/apiError";
import { Provider, ProviderWithStatistics } from "../models/Provider";
import { Paging } from "../models/Paging";
import useApi from "../services/ApiService";
import { ProviderFile } from "../models/ProviderFile";
import { ConflictGroupDto, ConflictResolutionItemDto } from "../models/ConflictGroup";
import { ConflictReasonCount } from "../models/ConflictReasonCount";
import {QUERY_CONFIG} from "../constants/QueryConfig";
import {EmptyFolder} from "../models/EmptyFolder";
import { MigrationFlagStatus } from "../models/Flags/MigrationFlagStatus";

export interface GetProvidersQueryOptions {
  organizationId?: string;
  status?: string[] | null;
  isLocked?: boolean | null;
  createdBy?: string[];
  providerNameId?: string[] | null;
  ids?: string[];
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
  hasFlaggedPaths?: boolean | null;
  hasConflicts?: boolean | null;
  /** Only entities whose flag is in one of these states. */
  flagStatus?: MigrationFlagStatus[] | null;
}
export class GetProviderFilesForProviderQueryOptions {
  status?: string[] | null;
  extension?: string[] | null;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
  filePath?: string | null;
  destinationPath?: string | null;
  minFileSize?: number | null;
  maxFileSize?: number | null;
  type?: number | null;
  failureType?: string | null;
  conflictReason?: string[] | null;
  conflictStatus?: string[] | null;
}

export class GetProviderEmptyFoldersForProviderQueryOptions {
  status?: string[] | null;
  uploadPathFromRoot?: string | null;
  pageNumber?: number;
  pageSize?: number;
  orderBy?: string;
  descending?: boolean;
}

export class ProvidersRepository {
  private api = useApi();

  async getProviders(
    options: GetProvidersQueryOptions = {}
  ): Promise<{ data: Provider[]; paging: Paging | null }> {
    const {
      status = null,
      isLocked = null,
      createdBy = null,
      providerNameId = null,
      ids = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      hasFlaggedPaths = null,
      hasConflicts = null,
      flagStatus = null,
    } = options;

    try {
      const response = await this.api.get("/api/Providers", {
        params: {
          Status: status,
          IsLocked: isLocked,
          CreatedBy: createdBy,
          ProviderNameId: providerNameId,
          Id: ids,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
          HasFlaggedPaths: hasFlaggedPaths,
          HasConflicts: hasConflicts,
          FlagStatus: flagStatus,
        },
        paramsSerializer: {
          indexes: true,
        },
      });

      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader) {
        paging = JSON.parse(paginationHeader || "");
      }

      return { data, paging };
    } catch (error) {
      // Handle error
      throw new Error("Failed to fetch providers");
    }
  }

  async getProvider(id: string): Promise<ProviderWithStatistics> {
    try {
      const response = await this.api.get(`/api/Providers/${id}`);
      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(`Failed to fetch provider with id: ${id}`);
    }
  }

  async getProviderFiles(
    providerId?: string,
    options: GetProviderFilesForProviderQueryOptions = {}
  ): Promise<{ data: ProviderFile[]; paging: Paging | null }> {
    const {
      status = null,
      extension = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
      filePath = null,
      destinationPath = null,
      minFileSize = null,
      maxFileSize = null,
      failureType = null,
      conflictReason = null,
      conflictStatus = null
    } = options;
    try {
      const apiUrl = providerId
        ? `/api/Providers/${providerId}/files`
        : "/api/ProviderFiles";
      const response = await this.api.get(apiUrl, {
        params: {
          Status: status,
          Extension: extension,
          FilePath: filePath,
          DestinationPath: destinationPath,
          MinFileSize: minFileSize,
          MaxFileSize: maxFileSize,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
          FailureType: failureType,
          ConflictReason: conflictReason,
          ConflictStatus: conflictStatus,
        },
        paramsSerializer: {
          indexes: true,
        },
        timeout: QUERY_CONFIG.DEFAULT_TIMEOUT,
      });
      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");
      
      return { data, paging };
    } catch (error) {
      const message = providerId
        ? `Failed to fetch files for provider with id: ${providerId}`
        : `Failed to fetch provider files`;
      throw new Error(message);
    }
  }

  async getProviderEmptyFoldersForProvider(
    id: string,
    options: GetProviderEmptyFoldersForProviderQueryOptions = {}
  ): Promise<{ data: EmptyFolder[]; paging: Paging | null }> {
    const {
      status = null,
      uploadPathFromRoot = null,
      pageNumber = null,
      pageSize = null,
      orderBy = null,
      descending = true,
    } = options;
    try {
      const response = await this.api.get(`/api/Providers/${id}/emptyFolders`, {
        params: {
          Status: status,
          UploadPathFromRoot: uploadPathFromRoot,
          PageNumber: pageNumber,
          PageSize: pageSize,
          OrderBy: orderBy,
          Descending: descending,
        },
        paramsSerializer: {
          indexes: true,
        },
        timeout: 100 * 1000,
      });

      const { data, headers } = response;
      let paging: Paging | null = null;
      const paginationHeader = headers["x-pagination"];
      if (paginationHeader)
        paging = JSON.parse(paginationHeader || "");

      return { data, paging };
    } catch (error) {
      // Handle error
      throw new Error(`Failed to fetch empty folder for provider with id: ${id}`);
    }
  }
  
  async updateProviderBlock(providerId: string, block: boolean): Promise<void> {
    try {
      const data: Record<string, any> = {
        entityId: providerId,
        block: block,
      };

      const response: AxiosResponse<void> = await this.api.patch(
        `/api/Providers/updateBlock`,
        data
      );

      return response.data;
    } catch (error) {
      // Handle error
      throw new Error(
        `Failed to update block info of provider with id: ${providerId}`
      );
    }
  }

  async markNotFoundFileForRetry(
    providerId: string,
    providerFileId: string,
    reason: string
  ): Promise<void> {
    try {
      const data: Record<string, any> = {
        migrationEntityId: providerId,
        fileId: providerFileId,
        reason: reason
      };
      await this.api.post(
        `/api/providerFiles/markNotFoundForRetry`,
        data
      );
    } catch (error) {
      throw new Error(`Failed to mark not found file for retry for providerId: ${providerId} and providerFileId: ${providerFileId}`);
    }
  }

  async getConflictGroups(providerId: string, includeResolved = false): Promise<ConflictGroupDto[]> {
    try {
      const response = await this.api.get(`/api/ProviderFiles/GetConflictGroups`, {
        params: { providerId: providerId, includeResolved: includeResolved },
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch conflict groups for provider with id: ${providerId}`);
    }
  }

  async getConflictReasonCounts(): Promise<ConflictReasonCount[]> {
    try {
      const response = await this.api.get(`/api/ProviderFiles/GetConflictReasonCounts`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch conflict reason counts for providers`);
    }
  }

  async resolveConflictGroup(resolutions: ConflictResolutionItemDto[]): Promise<void> {
    try {
      await this.api.post(`/api/ProviderFiles/ResolveConflictGroup`, {
        resolutions: resolutions,
      });
    } catch (error) {
      throw apiErrorWithMessages(error, "Failed to resolve conflict group");
    }
  }
}

export default ProvidersRepository;
